import { motion } from 'framer-motion';

interface ConnectionStateProps {
  /** Overall connection status */
  status: 'connecting' | 'connected' | 'reconnecting' | 'failed';
  /** Detailed status message */
  message?: string;
}

export function ConnectionState({ status, message }: ConnectionStateProps) {
  if (status === 'connected') {
    return null; // Don't show anything when connected
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="fixed top-4 left-1/2 -translate-x-1/2 z-50"
    >
      <div
        className={`px-6 py-3 rounded-full shadow-lg backdrop-blur-md ${
          status === 'failed'
            ? 'bg-red-500/90 text-white'
            : 'bg-brand-navy/90 text-white'
        }`}
      >
        <div className="flex items-center gap-3">
          {/* Spinner for connecting/reconnecting */}
          {(status === 'connecting' || status === 'reconnecting') && (
            <svg
              className="animate-spin h-5 w-5 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          )}

          {/* Error icon for failed */}
          {status === 'failed' && (
            <svg
              className="h-5 w-5 text-white"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="15" y1="9" x2="9" y2="15" />
              <line x1="9" y1="9" x2="15" y2="15" />
            </svg>
          )}

          <span className="font-medium">
            {message || getDefaultMessage(status)}
          </span>
        </div>
      </div>
    </motion.div>
  );
}

function getDefaultMessage(status: ConnectionStateProps['status']): string {
  switch (status) {
    case 'connecting':
      return 'Connecting...';
    case 'reconnecting':
      return 'Reconnecting...';
    case 'failed':
      return 'Connection failed';
    default:
      return '';
  }
}
